// src/modules/units/tyres/shared/tyre-column-prefs.js
//
// Persistence for the register's chosen columns. Stores ONLY an ordered array of
// column keys, in localStorage, scoped to the signed-in user.
//
// Per user, not per customer: which columns you want to see is a personal
// preference. localStorage is per-device anyway, so the key is scoped by the
// user's email to stop two people sharing a browser from overwriting each other.
// Versioned (v1) so a future shape change can be spotted and discarded rather
// than mis-read.
//
// Absence means default. We never write the default set — saveColumnKeys() clears
// the entry when the choice equals DEFAULT_COLUMN_KEYS — so a later change to the
// default reaches existing users instead of being frozen out by a stored
// snapshot. resolveColumns() validates the keys on read, so a stale key (a column
// renamed or dropped since it was saved) is discarded, not fatal.
//
// Every access is wrapped: localStorage can throw (private mode, quota), and a
// column preference must never be the thing that takes the register down.

import { getUserData } from '../../../auth/views/login/auth.api.js';
import { DEFAULT_COLUMN_KEYS } from './tyre-columns.js';

const VERSION = 'v1';

function storageKey() {
  const who = getUserData()?.eMail || 'anon';
  return `tm:register:columns:${VERSION}:${who}`;
}

function sameAsDefault(keys) {
  return keys.length === DEFAULT_COLUMN_KEYS.length &&
    keys.every((k, i) => k === DEFAULT_COLUMN_KEYS[i]);
}

/** The stored key array, or null when nothing is stored (meaning: use the default). */
export function loadColumnKeys() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (Array.isArray(parsed) && parsed.every((k) => typeof k === 'string')) ? parsed : null;
  } catch (e) {
    return null;
  }
}

/** Persist the chosen keys — or clear the entry when they equal the default. */
export function saveColumnKeys(keys) {
  try {
    if (!Array.isArray(keys) || sameAsDefault(keys)) {
      localStorage.removeItem(storageKey());
      return;
    }
    localStorage.setItem(storageKey(), JSON.stringify(keys));
  } catch (e) {
    // storage unavailable/full — non-fatal; the choice simply won't survive reload.
  }
}

/** Forget the stored choice (back to default). */
export function clearColumnKeys() {
  try { localStorage.removeItem(storageKey()); } catch (e) { /* non-fatal */ }
}
